'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { usePerformanceDebug } from '@/lib/debug-performance-hook';

interface ProjectsPageClientProps {
  projects: any[];
  types: any[];
}

export default function ProjectsPageClient({ projects, types: _types }: ProjectsPageClientProps) {
  usePerformanceDebug('ProjectsPageClient', 'PROJECTS_PAGE');
  
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Initialize from URL params
  useEffect(() => {
    const tab = searchParams.get('tab') || 'all';
    const view = searchParams.get('view') as 'grid' | 'list' || 'grid';
    setActiveTab(tab);
    setViewMode(view);
  }, [searchParams]);

  // Filter projects by tab
  const currentProjects = activeTab === 'all' 
    ? projects 
    : projects.filter((p: any) => p.communityTypes?.nodes?.some((t: any) => t.slug === activeTab));



  const ProjectCard = ({ project }: { project: any }) => (
    <Link 
      href={`/projects/${project.slug}`}
      className="group bg-white border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-soft cursor-pointer block"
    >
      {/* Project Image */}
      <div 
        className="h-48 bg-center bg-cover bg-no-repeat relative"
        style={{ 
          backgroundImage: `url('${project.featuredImage?.node?.sourceUrl || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop&auto=format"}')` 
        }}
      >
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all duration-300" />
        
        {/* View Count Badge */}
        {project.projectViews > 0 && (
          <div className="absolute top-4 right-4 bg-black/80 text-white px-2 py-1 rounded-full text-[12px] font-ibm-plex-sans-condensed">
            {project.projectViews.toLocaleString()} views
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        <h3 className="text-[20px] text-black mb-2 group-hover:text-[#e7131a] transition-colors" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
          {project.title}
        </h3>
        
        {/* Excerpt */}
        <div 
          className="font-ibm-plex-sans text-[14px] text-black/70 mb-4 line-clamp-2"
          dangerouslySetInnerHTML={{ __html: project.excerpt }}
        />
        
        {/* Meta Info */}
        <div className="flex items-center justify-between">
          {/* Tools */}
          {project.projectTools && (
            <span className="text-[12px] font-ibm-plex-sans-condensed text-black/50 uppercase tracking-wider">
              {project.projectTools}
            </span>
          )}
          
          {/* Type Tags */}
          <div className="flex gap-2">
            {project.communityTypes?.nodes?.map((type: any) => (
              <span 
                key={type.slug}
                className="bg-[#e7131a]/10 text-[#e7131a] px-2 py-1 text-[10px] font-ibm-plex-sans-condensed tracking-wider uppercase rounded"
              >
                {type.name}
              </span>
            ))}
          </div>
        </div>
        
        {/* Duration for audio/video */}
        {project.duration && (
          <div className="mt-2 text-[12px] font-ibm-plex-sans text-black/50">
            Duration: {project.duration}
          </div>
        )}
      </div>
    </Link>
  );

  return (
    <>
      {/* Projects Grid/List */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-white py-12 px-6 sm:px-8 lg:px-12">
          {currentProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-ibm-plex-sans text-[16px] text-black/70">
                No projects found in this category.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {currentProjects.map((project) => (
                <Link 
                  key={project.id}
                  href={`/projects/${project.slug}`}
                  className="flex gap-6 p-6 bg-white border border-gray-200 hover:shadow-soft cursor-pointer transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div 
                    className="w-32 h-32 bg-center bg-cover bg-no-repeat shrink-0"
                    style={{ 
                      backgroundImage: `url('${project.featuredImage?.node?.sourceUrl || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop&auto=format"}')` 
                    }}
                  />
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-[24px] text-black mb-2 hover:text-[#e7131a] transition-colors" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                      {project.title}
                    </h3>
                    <div 
                      className="font-ibm-plex-sans text-[14px] text-black/70 mb-4 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: project.excerpt }}
                    />
                    <div className="flex items-center gap-4 text-[12px] font-ibm-plex-sans-condensed text-black/50 uppercase tracking-wider">
                      {project.projectTools && <span>{project.projectTools}</span>}
                      {project.projectViews > 0 && <span>{project.projectViews.toLocaleString()} views</span>}
                      {project.duration && <span>{project.duration}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-black py-16 px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] text-white mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
              Share Your Creation
            </h2>
            <p className="font-ibm-plex-sans text-[16px] text-white/70 mb-8 max-w-2xl mx-auto">
              Join our community and showcase your AI-powered projects to thousands of enthusiasts worldwide.
            </p>
            <button className="bg-[#e7131a] text-white px-8 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-[#c10e14] hover:opacity-90">
              SUBMIT PROJECT
            </button>
          </div>
        </div>
      </div>
    </>
  );
}